import type { Tables } from '../../types/database.types';
import type { Profile } from '../auth/access';

export type Section = Tables<'sections'>;

export interface CreateSectionInput {
  projectId: string;
  name: string;
  position: number;
}

/** New sections land after the last one, in the same gapped scheme the database uses. */
export const SECTION_POSITION_GAP = 1000;

export function nextSectionPosition(sections: Section[]): number {
  const highest = sections.reduce((max, section) => Math.max(max, section.position), 0);
  return highest + SECTION_POSITION_GAP;
}

/**
 * Whether the UI should offer section management.
 *
 * Convenience only: can_manage_project() in the database re-derives this from
 * auth.uid() and refuses everyone else, so hiding a control here never grants
 * anything and never withholds anything the database would allow.
 */
export function canManageSections(profile: Profile, ownerId: string): boolean {
  if (!profile.is_active || profile.account_type !== 'tap') {
    return false;
  }
  // A global TAP Viewer stays read-only whatever their project role (contract
  // 4.3). That is stricter than can_manage_project(), which would still allow
  // a viewer who owns a project; the interface follows the contract and the
  // database remains the authority on anything that is actually attempted.
  if (profile.internal_role === 'tap_viewer') {
    return false;
  }
  return profile.internal_role === 'tap_admin' || profile.id === ownerId;
}

/**
 * Sections in display order. Archived sections are left out entirely: they are
 * hidden rather than deleted so their tasks survive (handoff 12).
 */
export function visibleSections(sections: Section[]): Section[] {
  return sections
    .filter((section) => !section.is_archived)
    .slice()
    .sort((left, right) => left.position - right.position);
}

/**
 * The position pair that swaps a section with its neighbour, or null when the
 * move would run off either end. Reordering swaps two rows rather than
 * renumbering the whole list.
 */
export function swapPositions(
  sections: Section[],
  sectionId: string,
  direction: 'up' | 'down',
): { id: string; position: number }[] | null {
  const ordered = visibleSections(sections);
  const index = ordered.findIndex((section) => section.id === sectionId);
  if (index === -1) {
    return null;
  }

  const neighbourIndex = direction === 'up' ? index - 1 : index + 1;
  const current = ordered[index];
  const neighbour = ordered[neighbourIndex];
  if (!current || !neighbour) {
    return null;
  }

  return [
    { id: current.id, position: neighbour.position },
    { id: neighbour.id, position: current.position },
  ];
}
